import "./InboundFlow.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { Menu, MenuItem } from "@mui/material";
import {
  faCircleXmark,
  faRotateLeft,
  faPhoneSlash,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { useContextStateManagement } from "../../context/ContextApi";
import {
  DEFAULT_PRACTICE_TIMEZONE,
  endOfDayInPracticeTimezone,
  formatInPracticeTimezone,
  startOfDayInPracticeTimezone,
} from "../../practiceTimezone";
import { getLatestLeadCallDate, buildLatestCallDateInRangeMap, buildCallStatsInRangeMap, safeDate } from "../../inboundCallHelpers";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../features/userSlice";
import { v4 as uuidv4 } from "uuid";
import { getUserLicenseDetails } from "../../Variables";
import { setIsBulkCallsEnabled } from "../../features/bulkCallsSlice";
import { useNavigate } from "react-router-dom";
import CallsBreakdownDialog from "../CallDialogs/CallsBreakdownDialog";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  List,
  Filter,
  MoreVertical,
} from "lucide-react";
import { PageActionMenu } from "../../components/PageActionMenu/PageActionMenu";
import CallFilterDialog from "../CallDialogs/CallFilterDialog";

const OUTBOUND_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const OUTBOUND_FILTERS_STORAGE_KEY = "cm_outboundFilters";
const OUTBOUND_FILTER_DEFAULTS = {
  range: "all",
  clientSearch: "",
  duration: "all",
  customStart: "",
  customEnd: "",
  missedCall: "all",
  voicemail: "all",
};
const OUTBOUND_FILTER_SECTIONS = [
  {
    title: "Range",
    fields: [
      {
        name: "range",
        label: "Range",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "24h", label: "24h" },
          { value: "7d", label: "7d" },
          { value: "30d", label: "30d" },
          { value: "custom", label: "Custom" },
        ],
      },
      {
        name: "customStart",
        label: "Custom Start",
        type: "date",
        showWhen: (values) => values.range === "custom",
      },
      {
        name: "customEnd",
        label: "Custom End",
        type: "date",
        showWhen: (values) => values.range === "custom",
      },
    ],
  },
  {
    title: "Outbound Filters",
    fields: [
      {
        name: "clientSearch",
        label: "Client Name & Number",
        placeholder: "Search by client name or number",
      },
      {
        name: "duration",
        label: "Duration",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "short", label: "Short <2 min" },
          { value: "medium", label: "Medium 2-5 min" },
          { value: "long", label: "Long >5 min" },
        ],
      },
      {
        name: "missedCall",
        label: "Missed Call",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
      {
        name: "voicemail",
        label: "Voicemail",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
];

const toDate = (value) => {
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

// getLatestLeadCallDate, not just row.CreateDateTime - a lead's CreateDateTime is frozen at
// first-ever contact and never advances for returning callers, so the Range filter's 24h/7d/30d
// presets (and its custom start/end bounds) were checking "when was this lead's record first
// created" instead of "does this lead have recent call activity". Uploads happen far less often
// than calls, so a narrow window like 24h could show 0 rows even with real same-day call
// activity, while a wider window (7d/30d) might coincidentally still catch a handful of
// creation dates and look like it "worked".
const getOutboundRowDate = (row) => getLatestLeadCallDate(row) || toDate(row?.date || null);

// The Duration filter buckets by a single call's length (Short/Medium/Long), but
// TotalDurationSec/TotalAllLegsDurationSec are cumulative across every attempt for
// the lead - a lead with several short calls would overflow straight into "Long"
// even though no individual call lasted that long. Use the most recent call
// attempt's own durationSec instead, falling back to the lead-level totals only
// when no per-call breakdown is available at all.
const getOutboundDurationSec = (row) => {
  const calls = Array.isArray(row?.Calls) ? row.Calls : [];
  const latestCall = calls.reduce((latest, entry) => {
    const entryDate = toDate(entry?.startedAt || entry?.CreateDateTime || entry?.createDateTime);
    const latestDate = latest
      ? toDate(latest?.startedAt || latest?.CreateDateTime || latest?.createDateTime)
      : null;
    if (!entryDate) return latest;
    return !latest || (entryDate && latestDate && entryDate > latestDate) ? entry : latest;
  }, null);

  if (latestCall) {
    return Number(latestCall?.durationSec) || 0;
  }

  return (
    Number(
      row?.TotalDurationSec ??
        row?.TotalAllLegsDurationSec ??
        row?.DurationSec ??
        row?.durationSec ??
        0,
    ) || 0
  );
};

// LeadGenerationUploadLead rows carry the caller's name as separate
// CallerFirstName/CallerLastName columns (no PatientFullName field exists
// on this table - that field was dropped for LeadGenerationUploadLead).
const getCallerFullName = (row) =>
  `${row?.CallerFirstName || ""} ${row?.CallerLastName || ""}`.trim();

const rowMatchesDuration = (durationSec, durationFilter) => {
  if (durationFilter === "all") return true;
  if (durationFilter === "short") return durationSec < 120;
  if (durationFilter === "medium") return durationSec >= 120 && durationSec <= 300;
  if (durationFilter === "long") return durationSec > 300;
  return true;
};

const getMissedCallCount = (row) => Number(row?.MissedCalls || 0);
const hadMissedCall = (row) => getMissedCallCount(row) > 0;
const getVoicemailCallCount = (row) => Number(row?.VoicemailCalls || 0);
const hadVoicemailCall = (row) => getVoicemailCallCount(row) > 0;

const rowMatchesYesNo = (isYes, filterValue) => {
  if (filterValue === "yes") return isYes;
  if (filterValue === "no") return !isYes;
  return true;
};

const getRangeStart = (rangeKey, customStart, timeZone) => {
  if (rangeKey === "24h") return new Date(Date.now() - 24 * 60 * 60 * 1000);
  if (rangeKey === "7d") return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  if (rangeKey === "30d") return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  // startOfDayInPracticeTimezone, not toDate/new Date(customStart) - a plain date-only string
  // parses as UTC midnight, which drifted the actual filter boundary by the practice's UTC
  // offset from what's displayed (rows are shown in practice-local time everywhere else in
  // this grid).
  if (rangeKey === "custom") return startOfDayInPracticeTimezone(customStart, timeZone);
  return null;
};
const OUTBOUND_EXPORT_HEADERS = [
  "Status",
  "Lead Created",
  "Client Name",
  "Client Number",
  "Assistant",
  "Total Attempts",
  "Answered",
  "Missed Call",
  "Voicemail",
  "Do Not Call",
];

const normalizeCsvText = (value) =>
  String(value ?? "")
    .replace(/\r?\n+/g, " | ")
    .replace(/\s+/g, " ")
    .trim();

const csvEscape = (value) => {
  const normalized = normalizeCsvText(value);

  if (/[",\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }

  return normalized;
};

// Excel auto-detects a date/time-looking string in a CSV and silently re-parses it into its own
// date serial number, which then renders as "#####" once the column isn't wide enough to show it
// (a plain text string never has this problem - only something Excel recognizes as a real date).
// ="..." forces Excel to keep it as literal text instead, no matter the column width.
const csvForceText = (value) => `="${normalizeCsvText(value).replace(/"/g, '""')}"`;

// A bare digit string (e.g. "16025550134") gets auto-converted by Excel into scientific
// notation (1.6E+10) or shown as ### once the column's too narrow - dash-separating it the way
// a phone number normally reads keeps it a plain text value in any spreadsheet app.
const formatCsvPhoneLikeValue = (value) => {
  const normalized = normalizeCsvText(value);
  if (!normalized || /^not provided$/i.test(normalized) || normalized === "-") {
    return normalized || "Not provided";
  }
  const digits = normalized.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `${digits[0]}-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return normalized;
};

// dateRange (optional): {startDate, endDate} day-keys - when given, Total Attempts/Answered/
// Missed Call/Voicemail are counted only from calls inside that range instead of the lead's
// all-time TotalCallAttempts/CallReceived/MissedCalls/VoicemailCalls fields, matching the same
// fix already applied to the grid's Call Details/Missed Call/Voicemail columns (those numbers
// used to stay all-time even while the CSV's own rows were already scoped to the date filter).
const buildOutboundCsv = (rows, timeZone = DEFAULT_PRACTICE_TIMEZONE, dateRange = null) => {
  const csvLines = [OUTBOUND_EXPORT_HEADERS.join(",")];
  const statsById = dateRange
    ? buildCallStatsInRangeMap(rows, dateRange.startDate, dateRange.endDate, timeZone)
    : null;

  rows.forEach((row) => {
    const stats = statsById
      ? statsById.get(row?.Id) || { attempts: 0, answered: 0, missed: 0, voicemail: 0 }
      : {
          attempts: row?.TotalCallAttempts ?? 0,
          answered: row?.CallReceived ?? 0,
          missed: getMissedCallCount(row),
          voicemail: getVoicemailCallCount(row),
        };
    csvLines.push(
      [
        csvEscape(row?.Status || "Not provided"),
        row?.CreateDateTime
          ? csvForceText(formatInPracticeTimezone(row.CreateDateTime, timeZone))
          : csvEscape("Not provided"),
        csvEscape(getCallerFullName(row) || "Not provided"),
        csvEscape(formatCsvPhoneLikeValue(row?.CallerPhoneNumber)),
        csvEscape(row?.ProviderName || "Not provided"),
        csvEscape(stats.attempts),
        csvEscape(stats.answered),
        csvEscape(stats.missed > 0 ? "Yes" : "No"),
        csvEscape(stats.voicemail > 0 ? "Yes" : "No"),
        csvEscape(row?.DoNotCall === true ? "Yes" : "No"),
      ].join(","),
    );
  });

  return csvLines.join("\r\n");
};

const downloadCsvFile = (content, fileName) => {
  const blob = new Blob(["\uFEFF", content], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};


export const LeadGGrid = ({
  setIsCallsDataGet,
  setIsUploadLead,
  setLeadGGridData,
  leadGGridData,
  providerName,
  patientName,
  startDate,
  endDate,
  isUploadLead,
  setOutBoundLeadsDetail,
  tableTopBarControls = null,
  filterNonce,
}) => {
  const [modalShow, setModalShow] = useState(false);
  const [activeModal, setActiveModal] = useState("");
  const [userId, setUserId] = useState("");
  const [menuId, setMenuId] = useState("");
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [transcriptRecord, setTranscriptRecord] = useState("");
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState("");
  const [isData, setIsData] = useState(false);
  const [callAPIMsg, setCallAPIMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [callsDialogRow, setCallsDialogRow] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuRow, setActionMenuRow] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  // True server-side pagination (matches Summary tab exactly) - each page change fetches
  // exactly that page directly from the backend and replaces leadGGridData outright. There is
  // no "loaded so far vs. true total" staging at all, so there's nothing that can read as stuck.
  const [totalRows, setTotalRows] = useState(0);
  // Bumped to force a re-fetch of the current page even when paginationModel/activeDateFilter
  // haven't actually changed (e.g. after deleting/updating a lead, or a bulk upload completing).
  const [refetchNonce, setRefetchNonce] = useState(0);
  // The practice's own configured timezone, resolved server-side alongside the date-filtered
  // rows above - rendering their timestamps converted to this same zone (instead of the
  // browser's local zone) is what keeps "what day" a call falls on agreeing with the filter.
  const [practiceTimezone, setPracticeTimezone] = useState(DEFAULT_PRACTICE_TIMEZONE);
  // The practice's own configured zone (server-resolved, real even when GeneralCallSettings has
  // no row at all for this practice) - distinct from practiceTimezone above, which is the
  // viewer's own effective zone. Passed to CallsBreakdownDialog as fixedTimezone below.
  const [fixedTimezone, setFixedTimezone] = useState(DEFAULT_PRACTICE_TIMEZONE);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(() => {
    try {
      const stored = sessionStorage.getItem(OUTBOUND_FILTERS_STORAGE_KEY);
      return stored ? { ...OUTBOUND_FILTER_DEFAULTS, ...JSON.parse(stored) } : OUTBOUND_FILTER_DEFAULTS;
    } catch (_) {
      return OUTBOUND_FILTER_DEFAULTS;
    }
  });

  // Remember the Outbound table's filters across tab switches and refreshes, mirroring the
  // sessionStorage pattern ContextApi.js already uses for sideBarLinkActive/activeTab.
  useEffect(() => {
    sessionStorage.setItem(OUTBOUND_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);
  // Which date range (if any) the current page/Load-more sequence is
  // scoped to - null means the default, unfiltered getLeadGenerationUploadLead view. Seeded
  // from the startDate/endDate props at mount (now always populated with a month-to-date
  // default - see useConsoleSearch) instead of hardcoding null, so the very first fetch already
  // hits the date-bounded, correctly-paginated SearchWithDateLeadGeneration endpoint instead of
  // the unbounded one.
  const [activeDateFilter, setActiveDateFilter] = useState(() =>
    startDate && endDate ? { startDate, endDate } : null,
  );
  // Two independent effects (providerName/patientName and PracticeId/token/isUploadLead) can each
  // trigger a fresh, non-append fetch as those values settle during mount/context load - with no
  // sequencing guard, whichever request happens to RESOLVE last wins, even if it was fired earlier
  // with stale params. This ref lets a fetch detect it's been superseded and skip applying its own
  // (possibly stale) result.
  const outboundFetchSeqRef = useRef(0);
  const isBulkCallsEnabled = useSelector(
    (state) => state.bulkCallsForLeadGen.isBulkCallsEnabled,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const actionMenuOpen = Boolean(actionMenuAnchor);

  const {
    deleteRequest,
    practiceContactNumber,
    outBoundContact,
    setActiveTab,
    timeoutIdRef,
    PracticeId,
    token,
    usersPostRequest,
    setSideBarLinkActive,
    setToken,
    dateRangeRequest,
    paginatedRequest,
    userType,
    callSettingsData,
    setUserType,
    fetchApiHandle,
    authorizedGetRequest,
    timezonePreferenceVersion,
  } = useContextStateManagement();

  // Schema fields let the Filter By dialog offer a filter for every custom column this
  // practice's outbound schema defines (see PracticeSchemaEditor/DynamicBulkUpload), on top of
  // the fixed range/duration/missed-call/voice-call filters above - custom field values live in
  // each lead's CallerInformation jsonb, not as top-level columns.
  const [schemaFields, setSchemaFields] = useState([]);

  useEffect(() => {
    if (!PracticeId || !token) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const response = await authorizedGetRequest(token, `/api/PracticeSchemaVersion/${PracticeId}`);
        if (!cancelled) {
          setSchemaFields(response?.schema?.Fields ?? []);
        }
      } catch (_) {
        if (!cancelled) setSchemaFields([]);
      }
    })();

    return () => {
      cancelled = true;
    };
    // authorizedGetRequest intentionally excluded - ContextApi.js doesn't memoize its functions,
    // so including it would refetch on every unrelated context re-render instead of only when
    // PracticeId/token actually change (same rationale as ManageLeadsPage.js's
    // loadSchemaAndProviders).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, token]);

  const schemaFilterFields = useMemo(
    () =>
      schemaFields.map((field) => ({
        name: `schema_${field.name}`,
        rawName: field.name,
        label: field.name,
        type: field.type === "boolean" ? "select" : "text",
        placeholder: field.type === "boolean" ? undefined : `Search by ${field.name}`,
        options:
          field.type === "boolean"
            ? [
                { value: "all", label: "All" },
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
              ]
            : undefined,
      })),
    [schemaFields],
  );

  const filterSections = useMemo(
    () =>
      schemaFilterFields.length
        ? [...OUTBOUND_FILTER_SECTIONS, { title: "Schema Fields", fields: schemaFilterFields }]
        : OUTBOUND_FILTER_SECTIONS,
    [schemaFilterFields],
  );

  // Adds a default for any newly-discovered schema field without disturbing filter values the
  // user already set - schemaFields loads asynchronously after this component's initial filters
  // state is created, so those keys don't exist yet on first render.
  useEffect(() => {
    if (!schemaFilterFields.length) return;

    setFilters((current) => {
      let changed = false;
      const next = { ...current };

      schemaFilterFields.forEach((field) => {
        if (!(field.name in next)) {
          next[field.name] = field.type === "select" ? "all" : "";
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [schemaFilterFields]);

  // Pulled out of the useMemo below so the same client-side filtering (search/duration/missed
  // call/voicemail/schema fields - everything /api/getLeadGenerationUploadLead itself can't
  // filter on) can also be applied to a freshly, fully fetched row set for "Download All",
  // instead of only ever running against whatever page(s) happen to already be loaded.
  const applyOutboundRowFilters = (rows, activeFilters, timeZone) => {
    const rangeStart = getRangeStart(activeFilters.range, activeFilters.customStart, timeZone);
    // endOfDayInPracticeTimezone, not toDate(filters.customEnd) - the old UTC-midnight boundary
    // sat at the very start of the selected end day, so for any UTC-behind practice timezone it
    // excluded almost the entire end day's rows instead of including all of it.
    const rangeEnd =
      activeFilters.range === "custom"
        ? endOfDayInPracticeTimezone(activeFilters.customEnd, timeZone)
        : new Date();
    const normalizedSearch = activeFilters.clientSearch.trim().toLowerCase();

    return (Array.isArray(rows) ? rows : []).filter((row) => {
      const rowDate = getOutboundRowDate(row);
      const durationSec = getOutboundDurationSec(row);

      if (rangeStart && (!rowDate || rowDate < rangeStart)) {
        return false;
      }

      if (rangeEnd && activeFilters.range === "custom" && rowDate && rowDate > rangeEnd) {
        return false;
      }

      if (normalizedSearch) {
        const searchTarget = [
          getCallerFullName(row),
          row?.CallerPhoneNumber,
        ]
          .map((value) => String(value || "").toLowerCase())
          .join(" ");

        if (!searchTarget.includes(normalizedSearch)) {
          return false;
        }
      }

      if (!rowMatchesDuration(durationSec, activeFilters.duration)) {
        return false;
      }

      if (!rowMatchesYesNo(hadMissedCall(row), activeFilters.missedCall)) {
        return false;
      }

      if (!rowMatchesYesNo(hadVoicemailCall(row), activeFilters.voicemail)) {
        return false;
      }

      for (const field of schemaFilterFields) {
        const filterValue = activeFilters[field.name];
        const cellValue = row?.CallerInformation?.[field.rawName];

        if (field.type === "select") {
          const isYes = cellValue === true || String(cellValue ?? "").toLowerCase() === "true";
          if (!rowMatchesYesNo(isYes, filterValue)) {
            return false;
          }
        } else if (filterValue) {
          const normalizedCellValue = String(cellValue ?? "").toLowerCase();
          if (!normalizedCellValue.includes(String(filterValue).toLowerCase())) {
            return false;
          }
        }
      }

      return true;
    });
  };

  // True once any filter beyond the server-side date range is engaged (range preset/custom,
  // duration, missed call, voicemail, search, or a schema field). None of these have a
  // server-side equivalent, so the true "how many rows actually match" figure can only be known
  // by fetching every row that matches the date filter and counting the client-side-filtered
  // result - unlike the unfiltered case, one raw server page can't tell you that on its own.
  const hasActiveClientFilter =
    filters.range !== "all" ||
    filters.duration !== "all" ||
    filters.missedCall !== "all" ||
    filters.voicemail !== "all" ||
    Boolean(filters.clientSearch?.trim()) ||
    schemaFilterFields.some((field) => {
      const value = filters[field.name];
      return field.type === "select" ? value !== "all" : Boolean(value);
    });

  const filteredLeadRows = useMemo(() => {
    const allFiltered = applyOutboundRowFilters(leadGGridData, filters, practiceTimezone);
    if (!hasActiveClientFilter) {
      // leadGGridData already holds exactly this page's rows (true server-side pagination) -
      // the filter pass above is a no-op here, so this already IS the current page.
      return allFiltered;
    }
    // leadGGridData holds every row matching the date filter in this mode (fetched all at
    // once) - slice out just the current page after filtering, so pageCount/footer math
    // (computed from totalRows, set to allFiltered.length at fetch time) lines up with what's
    // actually rendered.
    const offset = paginationModel.page * paginationModel.pageSize;
    return allFiltered.slice(offset, offset + paginationModel.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters,
    leadGGridData,
    schemaFilterFields,
    practiceTimezone,
    hasActiveClientFilter,
    paginationModel.page,
    paginationModel.pageSize,
  ]);

  useEffect(() => {
    if (PracticeId && token) {
      fetchApiHandle("generalCallSettings", "leadGTab");
    }
  }, [PracticeId, token, fetchApiHandle]);

  const getRowId = (row) => row?.Id ?? uuidv4();

  const handleCloseModal = () => {
    setModalShow(false);
    setTranscriptRecord("");
    setSummary("");
    setStatus("");
  };

  const handleOpenActionMenu = (event, row) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuRow(row);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuRow(null);
  };

  const menuHandle = (id) => {
    setActiveModal("menu");
    setMenuId(id);
    transcriptionsFetchHandle(id, PracticeId);
  };

  const summaryHandle = (id) => {
    setActiveModal("summary");
    setMenuId(id);
    transcriptionsFetchHandle(id, PracticeId);
  };

  const statusHandle = (id) => {
    setActiveModal("status");
    setMenuId(id);
    transcriptionsFetchHandle(id, PracticeId);
  };

  const deleteCallHandle = (id) => {
    setActiveModal("Delete");
    setUserId(id);
    transcriptionsFetchHandle(id, PracticeId);
  };

  const callQueCallHandle = (id) => {
    setUserId(id);
    setModalShow(true);
    setActiveModal("CallQue");
  };

  const doNotCallBtnHandle = (id) => {
    setUserId(id);
    setModalShow(true);
    setActiveModal("DoNotCall");
  };

  // Bounds the "Called ..." subline (Lead Created column below) to the selected date filter,
  // same fix Inbound's Date & Time column already has - getLatestLeadCallDate returns the
  // lead's all-time latest call regardless of the active range, which used to show a "Called"
  // date that could sit outside the range you'd just filtered to.
  const latestCallDateInRangeById = useMemo(
    () =>
      activeDateFilter
        ? buildLatestCallDateInRangeMap(
            leadGGridData,
            activeDateFilter.startDate,
            activeDateFilter.endDate,
            practiceTimezone,
          )
        : new Map(),
    [leadGGridData, activeDateFilter, practiceTimezone],
  );

  // Same reasoning as latestCallDateInRangeById above - the Call Details column's own inline
  // "X attempts / Y answered" label used to always read row.TotalCallAttempts/CallReceived
  // (all-time), even while a date filter was active.
  const callStatsInRangeById = useMemo(
    () =>
      activeDateFilter
        ? buildCallStatsInRangeMap(
            leadGGridData,
            activeDateFilter.startDate,
            activeDateFilter.endDate,
            practiceTimezone,
          )
        : new Map(),
    [leadGGridData, activeDateFilter, practiceTimezone],
  );

  // Shared by the Call Details, Missed Call, and Voicemail columns - all three used to read the
  // lead's all-time TotalCallAttempts/CallReceived/MissedCalls/VoicemailCalls fields even while a
  // date filter was active.
  const resolveRowCallStats = (row) =>
    activeDateFilter
      ? callStatsInRangeById.get(row?.Id) || { attempts: 0, answered: 0, missed: 0, voicemail: 0 }
      : {
          attempts: row?.TotalCallAttempts || 0,
          answered: row?.CallReceived || 0,
          missed: getMissedCallCount(row),
          voicemail: getVoicemailCallCount(row),
        };

  // The Call Details popup used to always show a lead's ENTIRE call history regardless of the
  // active date filter - same fix as Inbound's identical Call Details popup.
  const callsDialogFilteredCalls = useMemo(() => {
    const allCalls = Array.isArray(callsDialogRow?.Calls) ? callsDialogRow.Calls : [];
    if (!activeDateFilter) return allCalls;
    const dayFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: practiceTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return allCalls.filter((call) => {
      const entryDate = safeDate(call?.startedAt || call?.CreateDateTime);
      if (!entryDate) return false;
      const dayKey = dayFormatter.format(entryDate);
      return dayKey >= activeDateFilter.startDate && dayKey <= activeDateFilter.endDate;
    });
  }, [callsDialogRow, activeDateFilter, practiceTimezone]);

  const columns = [
    {
      field: "Status",
      headerName: "Status",
      flex: 0.95,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <button
          type="button"
          className="inbound-table__text-button"
          onClick={() => statusHandle(params.row.Id)}
        >
          View Status
        </button>
      ),
    },
    {
      field: "CreateDateTime",
      headerName: "Lead Created",
      flex: 1.15,
      minWidth: 180,
      // MUI's default sortingOrder is ['asc', 'desc', null] - a third click removes sorting
      // entirely and falls back to the natural (unsorted, date-unrelated) row order, which
      // reads as "random" for a date column. Restrict this column to just the two useful
      // states so clicking always toggles between ascending and descending.
      sortingOrder: ["asc", "desc"],
      // Deliberately the lead's own upload date, not its most recent call - renamed from
      // "Lead Date To" to "Lead Created" so it reads honestly as what it is. Since the table's
      // date-range filter now matches by call activity (see /api/SearchWithDateLeadGeneration),
      // a lead created outside the selected range can still legitimately show up here if it was
      // called within it - the second "Called ..." line makes that visible instead of the row
      // just looking like it violates the filter.
      renderCell: (params) => {
        const latestCallDate = activeDateFilter
          ? latestCallDateInRangeById.get(params.row?.Id) || null
          : getLatestLeadCallDate(params.row);
        const showCalledOn = Boolean(activeDateFilter) && !params.row?.IsPendingCall && latestCallDate;
        return (
          <span className="inbound-table__mono-value inbound-table__mono-value--stacked">
            <span>{params.value ? formatInPracticeTimezone(params.value, practiceTimezone) : "-"}</span>
            {showCalledOn ? (
              <span className="inbound-table__mono-subvalue">
                Called {formatInPracticeTimezone(latestCallDate, practiceTimezone)}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      field: "CallDetails",
      headerName: "Call Details",
      flex: 1.15,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => {
        const stats = resolveRowCallStats(params.row);
        return (
          <button
            type="button"
            className="outbound-table__details-button"
            onClick={() => setCallsDialogRow(params.row)}
          >
            <span>{stats.attempts} attempts</span>
            <span>{stats.answered} answered</span>
          </button>
        );
      },
    },
    {
      field: "MissedCall",
      headerName: "Missed Call",
      description: "Yes if at least one call attempt to this lead had no pickup at all (busy/no-answer/failed) - this excludes attempts that went to voicemail, which are tracked separately in the Voicemail column.",
      flex: 0.8,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => {
        const isMissed = resolveRowCallStats(params.row).missed > 0;
        return (
          <span className={`outbound-table__flag ${isMissed ? "is-missed" : ""}`}>
            {isMissed ? "Yes" : "No"}
          </span>
        );
      },
    },
    {
      field: "Voicemail",
      headerName: "Voicemail",
      description: "Yes if at least one call attempt to this lead was answered by voicemail/an answering machine - a lead can show both Missed Call and Voicemail as Yes if different attempts had different outcomes.",
      flex: 0.8,
      minWidth: 105,
      sortable: false,
      renderCell: (params) => {
        const isVoicemail = resolveRowCallStats(params.row).voicemail > 0;
        return (
          <span className={`outbound-table__flag ${isVoicemail ? "is-yes" : ""}`}>
            {isVoicemail ? "Yes" : "No"}
          </span>
        );
      },
    },
    {
      field: "ClientNameAndNumber",
      headerName: "Client Name & Number",
      flex: 1.55,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => (
        <div className="outbound-table__client">
          <span className="outbound-table__client-name">
            {getCallerFullName(params.row) || "-"}
          </span>
          <span className="outbound-table__client-number">
            {params.row.CallerPhoneNumber || "-"}
          </span>
        </div>
      ),
    },
    {
      field: "ProviderName",
      headerName: "Assistant",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "Delete",
      headerName: "Delete",
      flex: 0.7,
      minWidth: 88,
      sortable: false,
      renderCell: (params) => (
        <button
          type="button"
          className="outbound-table__icon-button"
          onClick={() => deleteCallHandle(params.row.Id)}
          aria-label="Delete lead"
        >
          <FontAwesomeIcon icon={faCircleXmark} />
        </button>
      ),
    },
    {
      field: "CallQueue",
      headerName: "Call Queue",
      flex: 0.85,
      minWidth: 102,
      sortable: false,
      renderCell: (params) => (
        <button
          type="button"
          // Backend sets DoNotCall = true on every call outcome once it resolves
          // (/api/updateLead), regardless of whether that outcome produced a
          // transcript - a failed/no-answer call has no Transcript but has still
          // definitively resolved and won't be auto-retried, so requiring Transcript
          // here left the icon showing "not called" for calls that already were.
          className={`outbound-table__icon-button ${
            params.row.DoNotCall === true ? "is-active" : ""
          }`}
          onClick={() => callQueCallHandle(params.row.Id)}
          aria-label="Requeue call"
        >
          <FontAwesomeIcon icon={faRotateLeft} />
        </button>
      ),
    },
    {
      field: "DoNotCall",
      headerName: "Do Not Call",
      flex: 0.85,
      minWidth: 108,
      sortable: false,
      renderCell: (params) => (
        <button
          type="button"
          className={`outbound-table__icon-button ${
            params.row.DoNotCall === true ? "is-danger" : ""
          }`}
          onClick={() => doNotCallBtnHandle(params.row.Id)}
          aria-label="Do not call"
        >
          <FontAwesomeIcon icon={faPhoneSlash} />
        </button>
      ),
    },
    {
      field: "Actions",
      headerName: "Actions",
      flex: 0.75,
      minWidth: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <div className="inbound-table__actions">
          <button
            type="button"
            className="inbound-table__menu-trigger"
            aria-label="Open actions"
            onClick={(event) => handleOpenActionMenu(event, params.row)}
          >
            <MoreVertical size={16} />
          </button>
        </div>
      ),
    },
  ].map((column) => ({
    align: "left",
    headerAlign: "left",
    ...column,
  }));

  // True server-side pagination, matching Summary tab exactly, EXCEPT when a client-only
  // filter (range/duration/missed call/voicemail/search/schema field) is active - none of those
  // can be expressed as a server query param, so in that case this instead fetches every row
  // matching the date filter, and totalRows/pageCount are based on the client-filtered count
  // (see hasActiveClientFilter/filteredLeadRows above) rather than the raw server total. This
  // is what keeps the "Showing X-Y of Z" footer accurate when a filter is engaged - basing it on
  // the raw per-page fetch's own total would silently ignore however many rows the filter
  // actually removed.
  async function fetchOutboundPage() {
    if (!PracticeId || !token) return;

    // Claim this fetch as the latest one. If a newer fetch starts before this one resolves,
    // its result must not be allowed to overwrite state with stale data - see the ref's
    // declaration above for why this exists (multiple dependencies can change close together).
    const seq = ++outboundFetchSeqRef.current;

    if (userType === "admin") {
      setLeadGGridData([]);
    }
    setIsData(true);

    const url = activeDateFilter
      ? "/api/SearchWithDateLeadGeneration"
      : "/api/getLeadGenerationUploadLead";
    const baseParams = activeDateFilter
      ? {
          PracticeId,
          startDate: activeDateFilter.startDate,
          endDate: activeDateFilter.endDate,
          practiceContactNumber: outBoundContact,
        }
      : {
          PracticeId,
          practiceContactNumber: outBoundContact,
        };
    const params = hasActiveClientFilter
      ? baseParams
      : {
          ...baseParams,
          limit: paginationModel.pageSize,
          offset: paginationModel.page * paginationModel.pageSize,
        };

    const result = await paginatedRequest(token, url, params);

    if (seq !== outboundFetchSeqRef.current) {
      // Superseded by a newer fetch while this one was in flight - drop this result entirely,
      // including the loading-flag cleanup, which the newer fetch already owns.
      return;
    }

    if (result.error === "Token is expired, login again") {
      setIsTokenExpired(true);
      setIsData(false);
      return;
    }

    if (result.practiceTimezone) {
      setPracticeTimezone(result.practiceTimezone);
    }

    if (result.fixedTimezone) {
      setFixedTimezone(result.fixedTimezone);
    }

    const rows = result.rows || [];
    setLeadGGridData(rows);
    setTotalRows(
      hasActiveClientFilter
        ? applyOutboundRowFilters(rows, filters, result.practiceTimezone || practiceTimezone).length
        : result.total,
    );
    setIsCallsDataGet(true);
    setIsData(false);
  }

  // Forces a re-fetch of whatever page is currently showing, resetting to page 1 first - used
  // after an action that changes the underlying list (delete/queue/do-not-call, bulk upload).
  const refetchFromFirstPage = () => {
    setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
    setRefetchNonce((n) => n + 1);
  };

  const formatter = (formattedDate, text) => `<b>${formattedDate}</b><pre>${text}</pre>\n`;

  async function transcriptionsFetchHandle(selectedMenuId, selectedPracticeId) {
    setTranscriptRecord("");
    setStatus("");
    setSummary("");
    setModalShow(true);

    const userData = await dateRangeRequest(token, "/api/getInOutboundCallsTranscripts", {
      leadId: selectedMenuId,
      memberListType: "LeadGenerationUploadLead",
      PracticeId: selectedPracticeId,
    });

    if (userData === "Token is expired, login again") {
      setIsTokenExpired(true);
      return;
    }

    if (userData === "no record") {
      return;
    }

    let transcriptionsText = "";
    let summariesText = "";
    let statusesText = "";

    // Only the records inside the selected date range - this used to always show every record
    // the lead ever had, even when the grid itself was already filtered down to a date range
    // (same fix already applied to Inbound's identical transcriptionsFetchHandle).
    const rawRecords = Array.isArray(userData) ? userData : [];
    const inRangeRecords = activeDateFilter
      ? (() => {
          const dayFormatter = new Intl.DateTimeFormat("en-CA", {
            timeZone: practiceTimezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
          return rawRecords.filter((record) => {
            const entryDate = record?.CreateDateTime ? new Date(record.CreateDateTime) : null;
            if (!entryDate || Number.isNaN(entryDate.getTime())) return false;
            const dayKey = dayFormatter.format(entryDate);
            return dayKey >= activeDateFilter.startDate && dayKey <= activeDateFilter.endDate;
          });
        })()
      : rawRecords;

    if (inRangeRecords.length > 0) {
      inRangeRecords.forEach((record) => {
        const formattedDate = new Date(record.CreateDateTime).toLocaleDateString(
          "en-US",
          {
            timeZone: practiceTimezone,
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          },
        );

        if (record.TranscriptText) {
          transcriptionsText += formatter(formattedDate, record.TranscriptText);
        }

        if (record.Summary) {
          summariesText += formatter(formattedDate, record.Summary);
        }

        if (record.Status) {
          statusesText += formatter(formattedDate, record.Status);
        }
      });
    }

    setTranscriptRecord(transcriptionsText);
    setSummary(summariesText);
    setStatus(statusesText);
  }

  const cancelCallHandle = async () => {
    const userData = await deleteRequest(token, "/api/deleteLead", {
      memberListType: "LeadGenerationUploadLead",
      Id: userId,
      practiceId: PracticeId,
    });

    if (userData === "Token is expired, login again") {
      setIsTokenExpired(true);
      return;
    }

    refetchFromFirstPage();
    getUserLicenseDetails(
      usersPostRequest,
      token,
      PracticeId,
      setIsTokenExpired,
      setOutBoundLeadsDetail,
    );
    setModalShow(false);
  };

  const queueCallHandle = async () => {
    const userData = await usersPostRequest(
      token,
      "/api/LeadGenerationUploadLeadDoNotCall",
      { doNotCall: false, id: userId },
    );

    if (userData === "Token is expired, login again") {
      setIsTokenExpired(true);
      return;
    }

    refetchFromFirstPage();
    setModalShow(false);
  };

  const doNotCallHandle = async () => {
    const userData = await usersPostRequest(
      token,
      "/api/LeadGenerationUploadLeadDoNotCall",
      { doNotCall: true, id: userId },
    );

    if (userData === "Token is expired, login again") {
      setIsTokenExpired(true);
      return;
    }

    refetchFromFirstPage();
    setModalShow(false);
  };

  const closeCallHandle = () => {
    setModalShow(false);
  };

  async function handleSearchInputChange() {
    const userData = await usersPostRequest(token, "/api/SearchLeadGenerationUploadLead", {
      patientName,
      providerName,
      PracticeId,
    });

    if (userData === "Token is expired, login again") {
      setIsTokenExpired(true);
      return;
    }

    const rows = Array.isArray(userData) ? userData : [];
    setLeadGGridData(rows);
    setTotalRows(rows.length);
    setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  }

  const getCurrentTimeInTimezone = (tz) => {
    const now = new Date();
    return now.toLocaleTimeString("en-US", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const parseToMinutes = (timeStr) => {
    const match = String(timeStr || "").match(/(\d{1,2})(?::(\d{2}))?\s?(AM|PM)/i);
    if (!match) return null;

    let [, hourStr, minuteStr, period] = match;
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr || "0", 10);

    if (period.toUpperCase() === "PM" && hour !== 12) {
      hour += 12;
    }
    if (period.toUpperCase() === "AM" && hour === 12) {
      hour = 0;
    }

    return hour * 60 + minute;
  };

  const triggerApiCall = async () => {
    try {
      const response = await usersPostRequest(token, "/api/getLeadsData", {
        practiceId: PracticeId,
        startDate: "2024-01-05",
        memberListType: "LeadGenerationUploadLead",
      });

      await usersPostRequest(token, "/api/storeBulkCallLogs", {
        practiceId: PracticeId,
        memberListType: "LeadGenerationUploadLead",
      });

      if (response === "No record") {
        dispatch(setIsBulkCallsEnabled(false));
        setCallAPIMsg("'No any record to call'");
        setTimeout(() => setCallAPIMsg(""), 5000);
        return false;
      }

      if (response && response.length > 0) {
        dispatch(setIsBulkCallsEnabled(true));
        setCallAPIMsg('"Leads fetched"');
        if (callSettingsData.length === 0) {
          setTimeout(() => {
            dispatch(setIsBulkCallsEnabled(false));
          }, 5000);
        }
        setTimeout(() => setCallAPIMsg(""), 5000);
        return true;
      }
    } catch (error) {
      setCallAPIMsg(`"${error.message} : Something went wrong"`);
      dispatch(setIsBulkCallsEnabled(false));
      setTimeout(() => setCallAPIMsg(""), 5000);
      return false;
    }

    return false;
  };

  const callMakeHandle = async (event) => {
    event.preventDefault();
    event.currentTarget.disabled = true;

    if (callSettingsData.length === 0) {
      await triggerApiCall();
      event.currentTarget.disabled = false;
      return;
    }

    const setting = callSettingsData[0];
    const userTimezone = setting.SelectedTimezone || "UTC";
    const currentDay = new Date().toLocaleDateString("en-US", {
      timeZone: userTimezone,
      weekday: "long",
    });
    const currentMinutes = parseToMinutes(getCurrentTimeInTimezone(userTimezone));
    const allowedDays = setting.DayOfCall || [];

    if (!allowedDays.includes(currentDay)) {
      setCallAPIMsg("Current day is not allowed for calling");
      setTimeout(() => setCallAPIMsg(""), 5000);
      event.currentTarget.disabled = false;
      return;
    }

    const batchInterval = parseInt(setting.BatchCallsTime, 10);
    const startMinutes = parseToMinutes(setting.StartTimeOfCall);
    const endMinutes = parseToMinutes(setting.EndTimeOfCall);

    if (currentMinutes >= startMinutes && currentMinutes <= endMinutes) {
      const runBatch = async () => {
        setLoading(true);
        let userData;
        try {
          userData = await usersPostRequest(token, "/api/checkTokenExpires", {});
        } finally {
          setLoading(false);
        }

        const now = new Date();
        const nowDay = now.toLocaleDateString("en-US", {
          timeZone: userTimezone,
          weekday: "long",
        });
        const nowMinutes = parseToMinutes(getCurrentTimeInTimezone(userTimezone));

        if (
          userData !== "Token is expired, login again" &&
          allowedDays.includes(nowDay) &&
          nowMinutes >= startMinutes &&
          nowMinutes <= endMinutes
        ) {
          const shouldContinue = await triggerApiCall();
          if (shouldContinue) {
            timeoutIdRef.current = setTimeout(runBatch, batchInterval * 60 * 1000);
          } else {
            timeoutIdRef.current = null;
            dispatch(setIsBulkCallsEnabled(false));
          }
        } else {
          timeoutIdRef.current = null;
          dispatch(setIsBulkCallsEnabled(false));
        }
      };

      await runBatch();
    } else {
      setCallAPIMsg("Current time is outside allowed calling hours");
      setTimeout(() => setCallAPIMsg(""), 5000);
    }

    event.currentTarget.disabled = false;
  };

  const callTerminateHandle = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
      dispatch(setIsBulkCallsEnabled(false));
      setCallAPIMsg("Bulk call timer has been terminated.");
      setTimeout(() => setCallAPIMsg(""), 5000);
    }
  };

  // Token-expiry redirect is now handled once, centrally, in ContextApi.js - see its comment
  // for why this component's own copy of the same effect was removed.

  // Name search (providerName/patientName, driven from Home.jsx's search box) hits its own
  // separate, non-paginated endpoint that returns every match at once - kept in its own effect,
  // keyed only on the search terms, exactly as before.
  useEffect(() => {
    if (providerName || patientName) {
      setSideBarLinkActive("careConsole");
      handleSearchInputChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerName, patientName]);

  // The main paginated fetch - runs whenever the practice, the current page/page size, the
  // active date filter, refetchNonce, or hasActiveClientFilter (switching between "one server
  // page" and "everything matching the date filter") changes. Skipped while a name search is
  // active (the effect above owns that case instead), same as before. Does NOT re-run on every
  // individual filter value tweak while already in "everything" mode - see the totalRows-sync
  // effect below for why that doesn't need a fresh fetch.
  useEffect(() => {
    if (!PracticeId || !token) return;
    if (providerName || patientName) return;
    fetchOutboundPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    PracticeId,
    token,
    providerName,
    patientName,
    paginationModel.page,
    paginationModel.pageSize,
    activeDateFilter,
    refetchNonce,
    hasActiveClientFilter,
    timezonePreferenceVersion,
  ]);

  // Keeps totalRows accurate while already in "everything matching the date filter" mode, as
  // the OTHER (non-date) filter values change - those don't require a fresh fetch (the full set
  // is already loaded), just a re-count against the new filter values.
  useEffect(() => {
    if (!hasActiveClientFilter) return;
    setTotalRows(applyOutboundRowFilters(leadGGridData, filters, practiceTimezone).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveClientFilter, leadGGridData, filters, practiceTimezone]);

  // A bulk upload completing (isUploadLead) should show the freshly uploaded leads from the top.
  useEffect(() => {
    if (isUploadLead) {
      setIsUploadLead(false);
      refetchFromFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadLead]);

  // Filter button (Home.jsx) bumps filterNonce - switch to the date-filtered, paginated
  // endpoint using whatever startDate/endDate are currently set (or back to the unfiltered one
  // if cleared), landing back on page 1 of the new filter. The actual fetch happens via the
  // main pagination effect above reacting to activeDateFilter/page changing.
  useEffect(() => {
    if (filterNonce) {
      // Empty startDate/endDate (filter cleared) must fall back to the unfiltered endpoint -
      // {startDate: "", endDate: ""} is still a truthy object, so it would otherwise keep
      // hitting SearchWithDateLeadGeneration with blank date params instead of reverting to
      // getLeadGenerationUploadLead.
      const hasDateFilter = Boolean(startDate) && Boolean(endDate);
      setActiveDateFilter(hasDateFilter ? { startDate, endDate } : null);
      setPaginationModel((current) => ({ ...current, page: 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterNonce]);

  // Clamp down if the current page no longer exists (e.g. pageSize grew, or the server-side
  // total shrank) - based on totalRows (the server's own count), not the client-filtered rows,
  // since a client-only filter narrowing the CURRENT page's visible rows doesn't mean there are
  // fewer real pages of data on the server.
  useEffect(() => {
    const nextPageCount = Math.max(1, Math.ceil(totalRows / paginationModel.pageSize));

    if (paginationModel.page > nextPageCount - 1) {
      setPaginationModel((current) => ({
        ...current,
        page: Math.max(0, nextPageCount - 1),
      }));
    }
  }, [totalRows, paginationModel.page, paginationModel.pageSize]);

  const handleExport = () => {
    const csvContent = buildOutboundCsv(filteredLeadRows, practiceTimezone, activeDateFilter);
    downloadCsvFile(
      csvContent,
      `LeadGenerationData_${moment().format("YYYYMMDD_HHmmss")}.csv`,
    );
  };

  // Download All - fetches every row matching the current date filter fresh from the backend
  // (omitting limit/offset puts /api/getLeadGenerationUploadLead and
  // /api/SearchWithDateLeadGeneration into their "send everything" mode, same convention Summary
  // tab's export already relies on) instead of exporting only whatever page(s) are currently
  // loaded in leadGGridData. The other, client-only filters (search/duration/missed
  // call/voicemail/schema fields) still get applied afterward via applyOutboundRowFilters, so the
  // export matches what's currently filtered on screen, just without the pagination cap.
  const handleExportAll = async () => {
    const effectiveFilter = activeDateFilter;
    const url = effectiveFilter
      ? "/api/SearchWithDateLeadGeneration"
      : "/api/getLeadGenerationUploadLead";
    const params = effectiveFilter
      ? {
          PracticeId,
          startDate: effectiveFilter.startDate,
          endDate: effectiveFilter.endDate,
          practiceContactNumber: outBoundContact,
        }
      : {
          PracticeId,
          practiceContactNumber: outBoundContact,
        };

    const result = await paginatedRequest(token, url, params);
    const allRows = applyOutboundRowFilters(
      result.rows || [],
      filters,
      result.practiceTimezone || practiceTimezone,
    );
    const csvContent = buildOutboundCsv(allRows, result.practiceTimezone || practiceTimezone, effectiveFilter);
    downloadCsvFile(
      csvContent,
      `LeadGenerationData_${moment().format("YYYYMMDD_HHmmss")}.csv`,
    );
  };

  const handleOpenPromptHub = () => {
    setSideBarLinkActive("onBoardingForm");
    navigate("/prompt-hub/outbound");
  };

  const handleFilterChange = (name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
    setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  };

  const handleResetFilters = () => {
    const schemaDefaults = {};
    schemaFilterFields.forEach((field) => {
      schemaDefaults[field.name] = field.type === "select" ? "all" : "";
    });
    setFilters({ ...OUTBOUND_FILTER_DEFAULTS, ...schemaDefaults });
    setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  };

  // Matches Summary tab's own footer/pagination exactly - true server pagination, so this is
  // "which raw rows of the true total are on this page," not a "loaded so far" count.
  const pageCount = Math.max(1, Math.ceil(totalRows / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page, pageCount - 1);
  const canGoPreviousPage = currentPage > 0;
  const canGoNextPage = currentPage < pageCount - 1;
  const rangeStart = totalRows > 0 ? currentPage * paginationModel.pageSize + 1 : 0;
  const rangeEnd = Math.min((currentPage + 1) * paginationModel.pageSize, totalRows);

  return (
    <>
      <div className="inbound-call-table outbound-call-table">
        <div className="inbound-call-table-topbar outbound-call-table-topbar">
          <div className="outbound-call-table-topbar-primary">
            <button
              onClick={callMakeHandle}
              disabled={isBulkCallsEnabled}
              className={`inbound-call-table-btn outbound-call-table-bulk-btn ${
                isBulkCallsEnabled ? "opacity-50 cursor-disabled" : ""
              } ${loading ? "spinner-padding" : ""}`}
            >
              {loading ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <>
                  <FontAwesomeIcon className="me-2" icon={faPhone} />
                  Start a bulk call
                </>
              )}
            </button>
            {isBulkCallsEnabled && timeoutIdRef.current ? (
              <button
                onClick={callTerminateHandle}
                className="inbound-call-table-btn inbound-call-table-btn--subtle outbound-call-table-terminate-btn"
              >
                <FontAwesomeIcon className="me-2" icon={faPhoneSlash} />
                Terminate
              </button>
            ) : null}
            {callAPIMsg ? (
              <span className="outbound-call-table-status-msg">{callAPIMsg}</span>
            ) : null}
          </div>
          <div className="inbound-call-table-topbar-controls">
            {tableTopBarControls}
            <button
              type="button"
              className="home-date-range-picker__trigger"
              onClick={() => setIsFilterDialogOpen(true)}
              aria-label="Open outbound filters"
            >
              <span className="home-date-range-picker__trigger-icon">
                <Filter size={16} strokeWidth={2.1} />
              </span>
              <span className="home-date-range-picker__trigger-text">Filter By</span>
            </button>
          </div>
          <div className="inbound-call-table-topbar-actions">
            <PageActionMenu
              onPromptHub={handleOpenPromptHub}
              onDownload={handleExportAll}
              onDownloadCurrentPage={handleExport}
              downloadLabel="Download All (CSV)"
              downloadCurrentPageLabel="Download Current Page (CSV)"
            />
          </div>
        </div>
        {activeDateFilter ? (
          <div className="call-activity-range-note">
            Showing leads with call activity in this range
          </div>
        ) : null}
        <div className="outbound-call-table-grid">
          <DataGrid
            apiRef={apiRef}
            rows={filteredLeadRows}
            rowHeight={48}
            columnHeaderHeight={48}
            scrollbarSize={8}
            columns={columns}
            pagination
            paginationMode="server"
            rowCount={totalRows}
            hideFooter
            autoWidth
            initialState={{ sorting: { sortModel: [{ field: "CreateDateTime", sort: "desc" }] } }}
            pageSizeOptions={OUTBOUND_PAGE_SIZE_OPTIONS}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            // Same fix as InBoundCalls.jsx - re-sorting changes which rows land on the current
            // page number, but nothing else here resets pagination on a sort change, so a row
            // could silently move to a different page while you stay put. Always land back on
            // page 1 of the new order instead.
            onSortModelChange={() => setPaginationModel((current) => ({ ...current, page: 0 }))}
            loading={userType === "admin" && isData === true}
            getRowId={getRowId}
            disableRowSelectionOnClick
            rowSelection={false}
            disableColumnMenu
            // IsPendingCall (set server-side only when a date range is active - see
            // /api/SearchWithDateLeadGeneration) marks a lead that has never been called at all
            // but is still shown because it was created within the selected range - dim it so
            // it reads as "uploaded, not called yet" instead of unexplained call activity.
            getRowClassName={(params) => (params.row?.IsPendingCall ? "inbound-table__row--pending" : "")}
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
                justifyContent: "flex-start",
                alignItems: "center",
                px: 1.75,
              },
              "& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-cellContent": {
                width: "100%",
                justifyContent: "flex-start",
                textAlign: "left",
              },
              "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                width: "100%",
                justifyContent: "flex-start",
              },
              "& .MuiDataGrid-cell": {
                py: 0,
              },
            }}
          />
        </div>
        <CallFilterDialog
          open={isFilterDialogOpen}
          title="Outbound Filters"
          description="Refine outbound rows without changing the primary date range."
          sections={filterSections}
          values={filters}
          onChange={handleFilterChange}
          onClose={() => setIsFilterDialogOpen(false)}
          onApply={() => setIsFilterDialogOpen(false)}
          onReset={handleResetFilters}
        />

        <div className="inbound-call-table-footer">
          <div className="inbound-call-table-footer-primary">
            <div className="inbound-call-table-count">
              {totalRows > 0 ? `Showing ${rangeStart}-${rangeEnd} of ${totalRows}` : ""}
            </div>
          </div>
          <div className="inbound-call-table-footer-controls">
            <div className="inbound-call-table-page-size">
              <span className="inbound-call-table-page-size-icon" aria-hidden="true">
                <List size={16} strokeWidth={2} />
              </span>
              <label
                htmlFor="outbound-call-table-page-size"
                className="inbound-call-table-sr-only"
              >
                Rows per page
              </label>
              <div className="inbound-call-table-page-size-select-wrap">
                <select
                  id="outbound-call-table-page-size"
                  className="inbound-call-table-page-size-select"
                  value={paginationModel.pageSize}
                  onChange={(event) => {
                    const nextPageSize = Number(event.target.value);
                    setPaginationModel({
                      page: 0,
                      pageSize: nextPageSize,
                    });
                  }}
                >
                  {OUTBOUND_PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <span
                  className="inbound-call-table-page-size-chevron"
                  aria-hidden="true"
                >
                  <ChevronDown size={16} strokeWidth={2} />
                </span>
              </div>
            </div>
            <span className="inbound-call-table-footer-divider" aria-hidden="true" />
            <div className="inbound-call-table-pagination">
              <button
                type="button"
                onClick={() =>
                  setPaginationModel((current) => ({
                    ...current,
                    page: Math.max(current.page - 1, 0),
                  }))
                }
                disabled={!canGoPreviousPage}
                className="inbound-call-table-nav-button"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} strokeWidth={2.1} />
              </button>
              <div className="inbound-call-table-page-indicator">
                {currentPage + 1} / {pageCount}
              </div>
              <button
                type="button"
                onClick={() =>
                  setPaginationModel((current) => ({
                    ...current,
                    page: Math.min(current.page + 1, pageCount - 1),
                  }))
                }
                disabled={!canGoNextPage}
                className="inbound-call-table-nav-button"
                aria-label="Next page"
              >
                <ChevronRight size={16} strokeWidth={2.1} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Menu
        anchorEl={actionMenuAnchor}
        open={actionMenuOpen}
        onClose={handleCloseActionMenu}
        PaperProps={{ className: "inbound-table__menu" }}
        MenuListProps={{ className: "inbound-table__menu-list" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          className="inbound-table__menu-item"
          onClick={() => {
            if (!actionMenuRow) return;
            handleCloseActionMenu();
            menuHandle(actionMenuRow.Id);
          }}
        >
          View Transcript
        </MenuItem>
        <MenuItem
          className="inbound-table__menu-item"
          onClick={() => {
            if (!actionMenuRow) return;
            handleCloseActionMenu();
            summaryHandle(actionMenuRow.Id);
          }}
        >
          View Summary
        </MenuItem>
      </Menu>

      <Modal className="popup" show={modalShow} onHide={() => setModalShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {activeModal === "menu"
              ? "Transcripts"
              : activeModal === "summary"
                ? "Summary"
                : activeModal === "status"
                  ? "Status"
                  : activeModal === "DoNotCall"
                    ? "Do Not Call"
                    : activeModal === "Delete"
                      ? "Delete Record"
                      : "Queue Call"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeModal === "menu" ? (
            transcriptRecord ? (
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(transcriptRecord) }} />
            ) : (
              ""
            )
          ) : activeModal === "summary" ? (
            summary ? (
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(summary) }} />
            ) : (
              ""
            )
          ) : activeModal === "status" ? (
            status ? (
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(status) }} />
            ) : (
              ""
            )
          ) : activeModal === "DoNotCall" ? (
            <p>Are you sure that you don't want to call this lead</p>
          ) : activeModal === "Delete" && transcriptRecord ? (
            <p>You can't delete this lead as it has been already processed</p>
          ) : activeModal === "Delete" ? (
            <p>Are you sure that you don't want to process call on this lead</p>
          ) : (
            <p>Are you sure that you want to requeue call on this lead again</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {activeModal === "menu" ||
          activeModal === "summary" ||
          activeModal === "status" ? (
            <button className="simple-btn" onClick={handleCloseModal}>
              Close
            </button>
          ) : activeModal === "Delete" && transcriptRecord ? (
            <button className="simple-btn me-2" id="closeTrue" onClick={closeCallHandle}>
              Close
            </button>
          ) : activeModal === "DoNotCall" ? (
            <>
              <button className="simple-btn me-2" id="closeTrue" onClick={queueCallHandle}>
                No
              </button>
              <button className="simple-btn" id="updateTrue" onClick={doNotCallHandle}>
                Yes
              </button>
            </>
          ) : activeModal === "Delete" ? (
            <>
              <button className="simple-btn me-2" id="closeTrue" onClick={closeCallHandle}>
                No
              </button>
              <button className="simple-btn" id="updateTrue" onClick={cancelCallHandle}>
                Yes
              </button>
            </>
          ) : (
            <>
              <button className="simple-btn me-2" id="closeTrue" onClick={closeCallHandle}>
                No
              </button>
              <button className="simple-btn" id="updateTrue" onClick={queueCallHandle}>
                Yes
              </button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <CallsBreakdownDialog
        open={callsDialogRow !== null}
        onClose={() => setCallsDialogRow(null)}
        filter="all"
        groupByConnection
        ambiguousNote={
          callsDialogRow?.CallHistoryMayBeShared
            ? "Another lead with this same phone number was uploaded at the same time - some of this call history may actually belong to that lead instead."
            : ""
        }
        calls={callsDialogFilteredCalls}
        leads={[]}
        totals={{
          // "Total Calls" needs to match the filtered list above, or the header would show a
          // stale all-time count while the table below only lists the in-range calls.
          total: activeDateFilter ? callsDialogFilteredCalls.length : (callsDialogRow?.TotalCallAttempts ?? 0),
          completed: callsDialogRow?.CallReceived ?? 0,
          durationSec: callsDialogRow?.TotalDurationSec ?? 0,
          billableDurationSec:
            callsDialogRow?.TotalAllLegsDurationSec ??
            callsDialogRow?.TotalDurationSec ??
            0,
          transferLegs: { calls: 0, durationSec: 0 },
        }}
        timeZone={practiceTimezone}
        fixedTimezone={fixedTimezone}
      />
    </>
  );
};
